import React, { act, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UniversityData } from '@/services/universityService';
import SupplementsSection from './SupplementsSection';

interface SchoolInfoProps {
  university: string;
  onSelectPrompt: (prompt: string, wordCount: string, type: string, promptId?: number) => void;
  universityData: UniversityData;
}

const SchoolInfo: React.FC<SchoolInfoProps> = ({ 
  university, 
  onSelectPrompt, 
  universityData 
}) => {
  const [activeTagIndex, setActiveTagIndex] = useState<number | null>(null);
  const navigate = useNavigate();

  const appTypes = universityData?.application_types?.map(appType => appType.name) || [];

  // Prepare supplement items
  const supplementItems = universityData.supplements.map((supplement) => ({
    id: supplement.id,
    name: `Supplement ${supplement.supplement_number}`,
    status: supplement.status,
    key: `supplement-${supplement.id}`,
    minPrompts: supplement.min_number_required
  }));

  // Prepare application type items
  const applicationItems = useMemo(() => {
    const items = universityData.application_types.map((appType) => ({
      id: appType.id,
      name: appType.name,
      status: appType.type,
      key: `application-${appType.id}`
    }));
   

    if(activeTagIndex === null) {
      return [];
    }
   
   return [items[activeTagIndex]];
  
  }, [activeTagIndex]);



  const handlePromptClick = (prompt: string, wordCount: string, type: string, promptId?: number) => {
    onSelectPrompt(prompt, wordCount, type, promptId);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 overflow-y-auto max-h-[530px] short:max-h-[400px]">
      <div className="university-header mb-6">
        <span className="university-name text-lg font-semibold text-gray-800 dark:text-gray-100">
          {universityData.university_name} - Application Requirements
        </span>
      </div>

      {universityData ? (
        <>
          <div className="app-types mb-6">
            <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">I am applying with the...</h4>
            <div className="tag-container flex flex-wrap gap-2">
              {appTypes?.map((item: string, index: number) => (
                <span
                  key={index}
                  className={`tag-item px-3 py-1 rounded-full text-sm cursor-pointer transition-colors ${
                    activeTagIndex === index 
                      ? 'bg-blue-600 dark:bg-blue-700 text-white' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                  onClick={() => setActiveTagIndex(index)}
                >
                  {item}
                </span>
              ))}
            </div>
          </div>

          {/* Application Types Section */}
          {universityData.application_types.length > 0 && (
            <div className="mb-6" data-tour="common-app">
              <SupplementsSection 
                items={applicationItems}
                title="Personal Statement"
                type="APPLICATION_TYPE"
                onSelectPrompt={handlePromptClick}
              />
            </div>
          )}

          {/* Supplements Section */}
          <div data-tour="supplements">
            <SupplementsSection 
              items={supplementItems}
              title="Supplements"
              type="SUPPLEMENT"
              onSelectPrompt={handlePromptClick}
            />
          </div>
        </>
      ) : (
        <div className="text-center text-gray-500 dark:text-gray-400">
          No university data available.
        </div>
      )}


    </div>
  );
};

export default SchoolInfo; 